import { Injectable, signal } from '@angular/core';

export type ConfirmationDialogOptions = {
  title?: string;
  message: string;
  detail?: string;
  confirmText?: string;
  cancelText?: string;
  tone?: 'danger' | 'default';
};

export type ConfirmationDialogState = Required<
  Pick<ConfirmationDialogOptions, 'message' | 'confirmText' | 'cancelText' | 'tone'>
> &
  Pick<ConfirmationDialogOptions, 'title' | 'detail'> & {
    isOpen: boolean;
  };

const DEFAULT_STATE: ConfirmationDialogState = {
  isOpen: false,
  title: 'Confirm action',
  message: '',
  detail: '',
  confirmText: 'Confirm',
  cancelText: 'Cancel',
  tone: 'default',
};

@Injectable({ providedIn: 'root' })
export class ConfirmationService {
  readonly dialog = signal<ConfirmationDialogState>(DEFAULT_STATE);

  private resolver: ((value: boolean) => void) | null = null;

  confirm(options: ConfirmationDialogOptions): Promise<boolean> {
    this.resolvePending(false);
    this.dialog.set({
      isOpen: true,
      title: options.title ?? DEFAULT_STATE.title,
      message: options.message,
      detail: options.detail ?? '',
      confirmText: options.confirmText ?? 'Confirm',
      cancelText: options.cancelText ?? 'Cancel',
      tone: options.tone ?? 'default',
    });

    return new Promise<boolean>((resolve) => {
      this.resolver = resolve;
    });
  }

  accept(): void {
    this.close(true);
  }

  dismiss(): void {
    this.close(false);
  }

  private close(result: boolean): void {
    this.dialog.set(DEFAULT_STATE);
    this.resolvePending(result);
  }

  private resolvePending(result: boolean): void {
    if (!this.resolver) {
      return;
    }

    const resolver = this.resolver;
    this.resolver = null;
    resolver(result);
  }
}
